import type { ConfigChange } from '@/types/robotEvents'

// 設定キーの表示名 (webapp_ros_mqtt_bridge の config_command / fork_webapp_react 車両設定)
const SETTING_LABELS: Record<string, string> = {
  speed_mode: '速度',
  obstacle_restart_wait_time: '障害物検知後の走行再開待ち時間',
  pallet_size: 'パレットサイズ',
}

// 変更元バッジ (config_changed の source / 起動時の再適用)
const SOURCE_LABELS: Record<string, { label: string; className: string }> = {
  local: { label: 'タッチパネル', className: 'bg-blue-100 text-blue-800' },
  aws_iot: { label: 'クラウド', className: 'bg-purple-100 text-purple-800' },
  restored: { label: '起動時再適用', className: 'bg-gray-100 text-gray-600' },
  unknown: { label: '不明', className: 'bg-gray-100 text-gray-600' },
}

function formatTime(ts: string): string {
  return new Date(ts).toLocaleTimeString('ja-JP', { hour12: false })
}

// 設定値を人が読める形にする (速度: 高速/低速、待ち時間: 秒、パレット: mm)
export function formatSettingValue(settingKey: string, value: unknown): string {
  if (value === null || value === undefined) return '－'
  if (settingKey === 'speed_mode') {
    if (value === 'normal') return '高速'
    if (value === 'low') return '低速'
    return String(value)
  }
  if (settingKey === 'obstacle_restart_wait_time') {
    return typeof value === 'number' ? `${value} 秒` : String(value)
  }
  if (settingKey === 'pallet_size' && Array.isArray(value)) {
    const [w, d] = value as unknown[]
    const mm = (m: unknown) =>
      typeof m === 'number' ? Math.round(m * 1000).toLocaleString('ja-JP') : '?'
    return `幅 ${mm(w)} × 奥行き ${mm(d)} mm`
  }
  return typeof value === 'object' ? JSON.stringify(value) : String(value)
}

interface ConfigChangeTableProps {
  changes: ConfigChange[]
  truncated?: boolean
  // 行クリックで再生位置をシークする
  onSeek?: (time: Date) => void
}

export default function ConfigChangeTable({
  changes,
  truncated,
  onSeek,
}: ConfigChangeTableProps) {
  return (
    <div className="overflow-x-auto">
      {truncated && (
        <p className="px-6 pt-2 text-xs text-amber-600">
          イベント数が多いため一部のみ表示しています
        </p>
      )}
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              時刻
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              設定
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              変更前
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              変更後
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              変更元
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {changes.map((c) => {
            const source = SOURCE_LABELS[c.source] ?? SOURCE_LABELS.unknown
            return (
              <tr
                key={c.key}
                className={onSeek ? 'hover:bg-gray-50 cursor-pointer' : ''}
                onClick={() => onSeek?.(new Date(c.ts))}
              >
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                  {formatTime(c.ts)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {SETTING_LABELS[c.settingKey] ?? c.settingKey}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  {formatSettingValue(c.settingKey, c.previous)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                  {formatSettingValue(c.settingKey, c.value)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span
                    className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${source.className}`}
                  >
                    {source.label}
                  </span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
